/* eslint-disable */
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FileCheck, Printer, RotateCcw } from "lucide-react";
import axiosInstance from "../../services/Axios/axios";
import RenderLoanPage from "../RenderLoanPage";
// Mounted in the admin shell too (/admin/clearance), same pattern as
// SettlementDetailed — one page, shell chosen from the pathname.
import RenderAdminPage from "../../AdminModel/RenderAdminPage";
import "./Clearance.css";

const DEPARTMENT_NAME = "UPNMG-FUND LOANS DEPARTMENT.";

const FUND_LOGO_URL =
  "https://upnmg-portal-bucket.s3.us-east-1.amazonaws.com/profile/fund-logo.png";
const SIGNATURE_IMAGE_URL =
  "https://upnmg-portal-bucket.s3.us-east-1.amazonaws.com/profile/Picture1.png";

const ONES = [
  "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = [
  "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
];

const belowThousand = (num) => {
  let result = "";
  if (num >= 100) {
    result += `${ONES[Math.floor(num / 100)]} Hundred`;
    num %= 100;
    if (num > 0) result += " and ";
  }
  if (num >= 20) {
    result += TENS[Math.floor(num / 10)];
    if (num % 10 > 0) result += `-${ONES[num % 10]}`;
  } else if (num > 0) {
    result += ONES[num];
  }
  return result;
};

const wholeToWords = (num) => {
  if (num === 0) return "Zero";
  const parts = [];
  const billions = Math.floor(num / 1_000_000_000);
  const millions = Math.floor((num % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((num % 1_000_000) / 1_000);
  const rest = num % 1_000;
  if (billions) parts.push(`${belowThousand(billions)} Billion`);
  if (millions) parts.push(`${belowThousand(millions)} Million`);
  if (thousands) parts.push(`${belowThousand(thousands)} Thousand`);
  if (rest) parts.push(belowThousand(rest));
  return parts.join(" ");
};

const amountToWords = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return "";
  const cedis = Math.floor(num);
  const pesewas = Math.round((num - cedis) * 100);
  const cedisWords = `${wholeToWords(cedis)} cedis`;
  if (pesewas > 0) {
    return `${cedisWords} ${wholeToWords(pesewas)} Pesewas`;
  }
  return cedisWords;
};

function Clearance() {
  const location = useLocation();
  const Shell = location.pathname.startsWith("/admin")
    ? RenderAdminPage
    : RenderLoanPage;

  const today = useMemo(() => new Date(), []);
  const auth = useSelector((state) => state.auth);
  const signatoryName = (auth?.user?.name || "Loan Officer").toUpperCase();
  const initialForm = {
    memberTitle: "Mr.",
    memberName: "",
    memberStaffId: "",
    settledDate: today.toISOString().split("T")[0],
    amountWords: "",
    amountPaid: "",
  };
  const [form, setForm] = useState(initialForm);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "amountPaid") {
        next.amountWords = amountToWords(value);
      }
      return next;
    });
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const ordinal = (day) => {
    if (day > 3 && day < 21) return `${day}th`;
    const suffix = ["th", "st", "nd", "rd"][day % 10] || "th";
    return `${day}${suffix}`;
  };

  const formatLongDate = (dateValue) => {
    const date = dateValue ? new Date(dateValue) : today;
    if (Number.isNaN(date.getTime())) return "";
    return `${ordinal(date.getDate())} ${date.toLocaleString("en-GB", {
      month: "long",
    })}, ${date.getFullYear()}`;
  };

  const formatAmount = (value) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-GH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const pronoun = form.memberTitle === "Mr." ? "his" : "her";
  const memberLastName = form.memberName.trim().split(" ").pop() || form.memberName;

  // Record the letter, then print. The POST is fire-and-forget on purpose: a
  // recording failure must never block the officer from issuing the letter, so
  // the print dialog opens either way. The record is what the Clearance
  // Dashboard counts.
  const issueClearance = () => {
    if (form.memberName.trim() && form.memberStaffId.trim()) {
      axiosInstance
        .post("/clearance", {
          member_name: form.memberName.trim(),
          staff_id: form.memberStaffId.trim(),
          member_title: form.memberTitle,
          amount_paid: Number(form.amountPaid) || 0,
          settled_date: form.settledDate || undefined,
          issued_by: auth?.user?.name || undefined,
        })
        .catch(() => {});
    }

    const previousTitle = document.title;
    document.title = " ";
    window.print();
    setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  };

  return (
    <Shell title="Clearance">
      <style>
        {`
          @media print {
            html,
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            body * {
              visibility: hidden;
            }
            #clearance-letter,
            #clearance-letter * {
              visibility: visible;
            }
            #clearance-letter {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              box-sizing: border-box;
              box-shadow: none !important;
              border: none !important;
            }
            @page {
              size: A4;
              margin: 0;
            }
          }
        `}
      </style>

      <div className="clearance-page">
        <section className="clearance-panel no-print">
          <div className="clearance-panel-header">
            <FileCheck size={20} />
            <div>
              <h3>Clearance Details</h3>
              <p>Issue a loan clearance letter.</p>
            </div>
          </div>

          <div className="clearance-form-grid">
            <label>
              Title
              <select
                name="memberTitle"
                value={form.memberTitle}
                onChange={handleChange}
              >
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </label>

            <label>
              Member Name
              <input
                name="memberName"
                value={form.memberName}
                onChange={handleChange}
                placeholder="e.g. AHMED ALHASSAN"
              />
            </label>

            <label>
              Member Staff ID
              <input
                name="memberStaffId"
                value={form.memberStaffId}
                onChange={handleChange}
                placeholder="e.g. 1411240"
              />
            </label>

            <label>
              Settled Date
              <input
                type="date"
                name="settledDate"
                value={form.settledDate}
                onChange={handleChange}
              />
            </label>

            <label>
              Amount Paid
              <input
                type="number"
                name="amountPaid"
                value={form.amountPaid}
                onChange={handleChange}
                placeholder="e.g. 2644.56"
                step="0.01"
                min="0"
              />
            </label>

          </div>

          <div className="clearance-actions">
            <button type="button" className="clearance-primary" onClick={issueClearance}>
              <Printer size={16} />
              Issue Clearance
            </button>
            <button type="button" className="clearance-secondary" onClick={resetForm}>
              <RotateCcw size={16} />
              Reset
            </button>
          </div>
        </section>

        <section id="clearance-letter" className="clearance-letter">
          <img
            className="clearance-watermark"
            src={FUND_LOGO_URL}
            alt=""
            aria-hidden="true"
          />
          <div className="clearance-left-rule"></div>
          <header className="clearance-letter-header">
            <div className="clearance-brand">
              <img
                className="clearance-fund-logo"
                src={FUND_LOGO_URL}
                alt="UPNMG Fund logo"
              />
              <div className="clearance-brand-copy">
                <strong>UPNMG</strong>
                <strong>FUND</strong>
                <span>wealth, welfare and Beyond</span>
              </div>
            </div>

            <div className="clearance-address">
              <p>
                <strong>Postal Address:</strong> P.O Box mrs 35, Mile 7 - Accra
              </p>
              <p>
                <strong>Tel:</strong> +233 24 221 5305/+233 24 221 5307
              </p>
              <p>
                <strong>E-mail:</strong> upnmgfund@gmail.com
              </p>
            </div>
          </header>

          <div className="clearance-ref-row">
            <span>Our Ref: ................................</span>
            <span>Your Ref: ................................</span>
            <span>Date: {formatLongDate(today)}</span>
          </div>

          <div className="clearance-meta">
            <p>
              <strong>TO:</strong> {form.memberTitle} {form.memberName || "........................"} (
              {form.memberStaffId || "................"})
            </p>
            <p>
              <strong>FROM:</strong> UPNMG - FUND LOANS
            </p>
            <p>
              <strong>DATE:</strong> {formatLongDate(today)}
            </p>
            <p>
              <strong>SUBJECT:</strong> CLEARANCE OF LOAN
            </p>
          </div>

          <div className="clearance-divider"></div>

          <div className="clearance-body">
            <p>
              This is to certify that {form.memberTitle} {form.memberName} (
              {form.memberStaffId}) has settled {pronoun} loan balance on{" "}
              {formatLongDate(form.settledDate)} with UPNMG-FUND Loans after
              receiving a settlement quotation from the Loans department.
            </p>

            <p>
              An Amount of {form.amountWords || "................................"} Only{" "}
              {form.amountPaid ? `(GHC${formatAmount(form.amountPaid)})` : "(GHC........)"}
              {" "}was paid. {form.memberTitle} {memberLastName}'s (
              {form.memberStaffId}) loan deduction has been terminated fully from
              the CAGD TPRS Platform after confirmation from the bank and payment
              receipt was received. This concludes {pronoun} transaction with
              UPNMG-FUND Loans.
            </p>
          </div>

          <div className="clearance-signature-area">
            <img
              className="clearance-signature-image"
              src={SIGNATURE_IMAGE_URL}
              alt="Signature and stamp"
            />
          </div>

          <footer className="clearance-footer">
            <strong>{signatoryName}</strong>
            <span>{DEPARTMENT_NAME}</span>
          </footer>
        </section>
      </div>

    </Shell>
  );
}

export default Clearance;
